import { useState, useRef, useEffect, createContext } from 'react';
import { t } from 'ttag';
import { DEFAULT_MAP_COORDS } from 'constants.js';
import { ReactComponent as IconLocationDot } from 'static/Common/icon-location-dot.svg';
import { ReactComponent as IconChevronUp } from 'static/Common/icon-chevron-up.svg';
import MapComponent from 'components/MapComponent/index.js';
import SiteListItem from './SiteListItem/index.js';
import './index.scss';

export const SiteListContext = createContext(null);

const MarkerContent = ({ name, location, onClick }) => (
    <>
        <h3>{name}</h3>
        <span className='site-info'>
            <IconLocationDot />
            {[location?.address, location?.city].filter(v => v).join(', ') || '-'}
        </span>
        <button type='button' onClick={onClick} className='btn--green-s'>
            {t`(ELV_STATION_LIST_MAP)_BTN_MARKER_POPUP_DETAIL_LABEL`}
        </button>
    </>
);

const ELVStationListMap = ({
    sites, marker,
    onClickExpand, onClickOpenList,
    listItemHeaderChildren, listItemChildren, children,
}) => {
    const refMap = useRef(null);

    const [expanded, setExpanded] = useState({});
    const [isMapExpanded, setIsMapExpanded] = useState(false);
    const [isListOpen, setIsListOpen] = useState(false);

    const _onClickExpand = state => {
        onClickExpand?.(state);
        setIsMapExpanded(state);
    };

    const _onClickOpenList = state => {
        onClickOpenList?.(state);
        setIsListOpen(state);
    };

    const onClickMarker = (id, i) => {
        const isExpanded = !expanded[id];

        setExpanded({ ...expanded, [id]: isExpanded });

        isExpanded && setTimeout(
            () => document.getElementsByClassName('list-item-toggle')[i]?.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
            }),
            2 ** 7,
        );
    };

    const markers = [
        ...(marker ? [marker] : []),
        ...sites
            .filter(v => v?.location?.lat && v.location.lng)
            .map(({ id, name, location }, i) => ({
                pos: [location?.lat, location?.lng],
                content: <MarkerContent name={name} location={location} onClick={() => _onClickOpenList(true)} />,
                onClick: () => onClickMarker(id, i),
                onMouseOver: () => document.getElementsByClassName('site-list-item')[i]?.classList.add('pointed'),
                onMouseOut: () => document.getElementsByClassName('site-list-item')[i]?.classList.remove('pointed'),
            })),
    ];

    useEffect(() => {
        const { current } = refMap;

        if(current) {
            current.invalidateSize();
            markers.length && current.fitBounds([markers.map(v => v.pos)], { maxZoom: current.getZoom() });
        }
    }, [sites, marker]);

    const onCreated = map => refMap.current = map;
    const onClickSite = id => setExpanded({ ...expanded, [id]: !expanded[id] });

    const onMouseLeaveListItem = () => document.querySelectorAll('.map-default-marker-icon.hover')
        .forEach(node => node.classList.remove('hover'));

    const onMouseEnterListItem = e => {
        onMouseLeaveListItem();

        const nodeLi = e.target.closest('li');
        const i = [...nodeLi.parentNode.children].indexOf(nodeLi) + 1;
        document.getElementsByClassName('map-default-marker-icon')[i]?.classList.add('hover');
    };

    return (
        <div
            className={
                `elv-station-list-map ${isMapExpanded ? 'is-map-expanded' : ''} ${isListOpen ? 'is-list-open' : ''}`
            }
        >
            {!!sites?.length && (
                <div className='list-wrap'>
                    <h2 className='list-title'>
                        {t`(ELV_STATION_LIST_MAP)_LIST_TITLE`}
                        <button
                            type='button'
                            onClick={() => _onClickOpenList(!isListOpen)}
                            className='btn-list-toggle'
                            title={isListOpen
                                ? t`(ELV_STATION_LIST_MAP)_BTN_MOBILE_LIST_CLOSE_TOOLTIP`
                                : t`(ELV_STATION_LIST_MAP)_BTN_MOBILE_LIST_OPEN_TOOLTIP`}
                        >
                            <IconChevronUp />
                        </button>
                    </h2>
                    {!!children && <div className='list-extra'>{children}</div>}
                    <ol className='site-list'>
                        {sites.map(site => (
                            <SiteListItem
                                key={site.id}
                                site={site}
                                isExpanded={expanded[site.id]}
                                onClick={onClickSite}
                                onMouseEnter={onMouseEnterListItem}
                                onMouseLeave={onMouseLeaveListItem}
                                headerChildren={listItemHeaderChildren}
                            >
                                {listItemChildren}
                            </SiteListItem>
                        ))}
                    </ol>
                </div>
            )}
            <MapComponent
                center={DEFAULT_MAP_COORDS}
                markers={markers}
                zoom={7}
                onCreated={onCreated}
                expandable
                trackResize
                onClickToggleExpanded={_onClickExpand}
            />
        </div>
    );
};

export default ELVStationListMap;
